import {
  Autocomplete,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { DomainRule, PermissionSetDomain, PermissionSetRules, RecordScope } from '../../../types';
import { Tag } from '../../../store/Tags';
import { DomainConfig, PERMISSION_SET_DOMAINS } from './domains';

const SCOPE_OPTIONS: { value: RecordScope; label: string }[] = [
  { value: 'all', label: 'All Account Data' },
  { value: 'tag', label: 'Tag-Based' }
];

// Sets saved before the api refused this shape still carry it, and dropping the option would leave a native select rendering a value it does not list.
const scopeOptionsFor = (config: DomainConfig, scope: RecordScope) =>
  SCOPE_OPTIONS.filter((option) => option.value !== 'tag' || config.tagScopable || scope === 'tag');

const HEADERS = ['Portal Area', 'Enabled', 'Write Access', 'Record Scope', 'Tags', 'Field Restrictions'];

export interface DomainMatrixProps {
  rules: PermissionSetRules;
  tagOptions: Tag[];
  defaultWrite: boolean;
  onChange: (rules: PermissionSetRules) => void;
  fieldSummaryFor?: (domain: PermissionSetDomain) => string;
  onEditFields?: (domain: PermissionSetDomain) => void;
  readOnly?: boolean;
}

interface DomainRowProps extends Omit<DomainMatrixProps, 'rules' | 'onChange'> {
  config: DomainConfig;
  rule?: DomainRule;
  onChange: (key: PermissionSetDomain, rule: DomainRule | null) => void;
}

// Names the consequence, not just the remedy: an admin needs to know the restriction never held, not which option to pick.
const inertScopeMessage = (label: string): string =>
  `Tag scoping is not applied to ${label} — this set shows every record there. Choose All Account Data.`;

const DomainRow = ({
  config,
  rule,
  tagOptions,
  defaultWrite,
  onChange,
  fieldSummaryFor,
  onEditFields,
  readOnly
}: DomainRowProps) => {
  const enabled = !!rule;
  const scope = rule?.record_scope ?? 'all';
  // Left visible and left failing rather than coerced to 'all', which would silently drop a restriction an admin believes is in force.
  const scopeUnenforceable = scope === 'tag' && !config.tagScopable;

  const patch = (next: DomainRule | null) => onChange(config.key, next);

  const handleScopeChange = (value: RecordScope) => {
    const next: DomainRule = { ...rule, record_scope: value };
    if (value !== 'tag') delete next.record_scope_tags;
    patch(next);
  };

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{config.label}</Typography>
      </TableCell>
      <TableCell>
        <Switch
          checked={enabled}
          disabled={readOnly}
          inputProps={{ 'aria-label': `${config.label} enabled` }}
          onChange={(event) => patch(event.target.checked ? { write: defaultWrite, record_scope: 'all' } : null)}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={rule?.write ?? false}
          disabled={!enabled || readOnly}
          inputProps={{ 'aria-label': `${config.label} write` }}
          onChange={(event) => patch({ ...rule, write: event.target.checked })}
        />
      </TableCell>
      <TableCell>
        <TextField
          select
          size="small"
          label={`${config.label} scope`}
          value={scope}
          disabled={!enabled || readOnly}
          error={scopeUnenforceable}
          helperText={scopeUnenforceable ? inertScopeMessage(config.label) : undefined}
          SelectProps={{ native: true }}
          onChange={(event) => handleScopeChange(event.target.value as RecordScope)}>
          {scopeOptionsFor(config, scope).map((option) => (
            <option
              key={option.value}
              value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        {enabled && scope === 'tag' && !scopeUnenforceable ? (
          <Autocomplete
            multiple
            size="small"
            readOnly={readOnly}
            options={tagOptions}
            getOptionLabel={(option) => option.name}
            value={tagOptions.filter((tag) => (rule?.record_scope_tags ?? []).includes(tag.id))}
            onChange={(_event, selected) => patch({ ...rule, record_scope_tags: selected.map((tag) => tag.id) })}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${config.label} tags`}
              />
            )}
          />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <FieldRestrictionsCell
          config={config}
          rule={rule}
          enabled={enabled}
          fieldSummaryFor={fieldSummaryFor}
          onEditFields={readOnly ? undefined : onEditFields}
        />
      </TableCell>
    </TableRow>
  );
};

interface FieldRestrictionsCellProps {
  config: DomainConfig;
  rule?: DomainRule;
  enabled: boolean;
  fieldSummaryFor?: (domain: PermissionSetDomain) => string;
  onEditFields?: (domain: PermissionSetDomain) => void;
}

const FieldRestrictionsCell = ({
  config,
  rule,
  enabled,
  fieldSummaryFor,
  onEditFields
}: FieldRestrictionsCellProps) => {
  if (!enabled)
    return (
      <Typography
        variant="body2"
        color="text.secondary">
        —
      </Typography>
    );
  const count = rule?.hide_fields?.length ?? 0;
  const summary = fieldSummaryFor ? fieldSummaryFor(config.key) : count === 0 ? 'None hidden' : `${count} hidden`;
  if (!onEditFields) return <Typography variant="body2">{summary}</Typography>;
  return (
    <button
      type="button"
      className="text-sm text-main-100 underline hover:text-main-200"
      onClick={() => onEditFields(config.key)}>
      {summary}
    </button>
  );
};

export const DomainMatrix = ({ rules, onChange, ...rowProps }: DomainMatrixProps) => {
  const updateDomain = (key: PermissionSetDomain, nextRule: DomainRule | null) => {
    const next = { ...rules };
    if (nextRule === null) delete next[key];
    else next[key] = nextRule;
    onChange(next);
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {HEADERS.map((header) => (
            <TableCell key={header}>{header}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {PERMISSION_SET_DOMAINS.map((config) => (
          <DomainRow
            key={config.key}
            config={config}
            rule={rules[config.key]}
            onChange={updateDomain}
            {...rowProps}
          />
        ))}
      </TableBody>
    </Table>
  );
};
